import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/shiksha-db';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAuth } from '../hooks/useAuth';
import { MARKING_SCHEMES } from '../config/marking-schemes';
import { TutorialTour } from '../components/TutorialTour';
import {
  Users,
  ClipboardCheck,
  BookMarked,
  Plus,
  RefreshCw,
  QrCode,
  Clock,
  ChevronRight,
  FileText
} from 'lucide-react';
import { ReportCardDownloadButton } from '../components/ReportCardDownloadButton';

/**
 * Dashboard — Teacher landing page
 * Dynamic class assignments from Dexie DB + MARKING_SCHEMES.
 * Real recent activity from activityLog table.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const { pendingSyncCount, isSyncing, triggerSync } = useOfflineSync();
  const navigate = useNavigate();

  const [academicYear, setAcademicYear] = useState('2026-27');
  const [section, setSection] = useState('A');

  const studentCount = useLiveQuery(() => db.students.count());
  const attendanceToday = useLiveQuery(() => {
    const today = new Date().toISOString().split('T')[0];
    return db.attendance.where('date').equals(today).count();
  });

  const teacherName = user?.displayName || user?.email?.split('@')[0] || 'Teacher';

  const teacherProfile = useLiveQuery(
    () => (user?.email ? db.teachers.where('email').equals(user.email).first() : null),
    [user?.email]
  );
  const students = useLiveQuery(() => db.students.toArray()) || [];

  // Recent activity from activityLog (real data)
  const recentActivity = useLiveQuery(async () => {
    try {
      return await db.activityLog.orderBy('timestamp').reverse().limit(5).toArray();
    } catch {
      return [];
    }
  }) || [];

  // Derive assigned classes dynamically
  // Strategy: Look at unique class values from students table, map to MARKING_SCHEMES keys
  const [assignedClasses, setAssignedClasses] = useState([]);

  useEffect(() => {
    // If admin, show all classes
    if (user?.role === 'admin' || teacherProfile?.role === 'admin') {
      if (!students || students.length === 0) {
        setAssignedClasses(Object.keys(MARKING_SCHEMES).map(key => ({
          id: key, name: key, subjectId: 'science', subject: 'All Subjects', students: 0
        })));
        return;
      }
      const classGroups = {};
      students.forEach(s => {
        const cls = resolveClassCohort(s.class);
        if (!classGroups[cls]) classGroups[cls] = 0;
        classGroups[cls]++;
      });
      const classes = Object.entries(classGroups).map(([key, count]) => ({
        id: key, name: key, subjectId: key.toLowerCase().replace(/\s+/g, '-'), subject: 'All Subjects', students: count
      }));
      setAssignedClasses(classes);
      return;
    }

    // If teacher, show their specifically assigned subjects
    if (teacherProfile?.subjectsAssigned?.length > 0) {
      const classes = teacherProfile.subjectsAssigned.map(sub => {
        const baseCohort = resolveClassCohort(sub.classId);
        
        // Extract section if present (e.g., "Class VIII-B" -> "B")
        const upperClassId = (sub.classId || '').toUpperCase();
        const parts = upperClassId.split('-');
        const sectionStr = parts.length > 1 ? parts[1].trim() : '';

        const studentCount = students ? students.filter(s => {
          if (resolveClassCohort(s.class) !== baseCohort) return false;
          if (!sectionStr) return true; // Show all sections for this class if no section specified in assignment
          
          const sSection = (s.section || '').toUpperCase().trim();
          // Cross-pollination: if a student has NO section, they belong to all sections
          if (!sSection) return true;
          return sSection === sectionStr;
        }).length : 0;
        
        return {
          id: sub.classId,
          name: sub.className,
          subjectId: sub.subjectId,
          subject: sub.subjectName,
          students: studentCount
        };
      });
      setAssignedClasses(classes);
    } else {
      setAssignedClasses([]);
    }
  }, [students, teacherProfile, user]);

  return (
    <div className="min-h-screen bg-paper pb-8">
      <TutorialTour type="teacher" />
      {/* ── Welcome Banner ── */}
      <div className="bg-indigo-600 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Welcome, {teacherName}</h1>
          <p className="text-indigo-100 font-secondary">Here's your overview for today.</p>
        </div>
      </div>

      {/* ── Stats & Actions ── */}
      <div className="max-w-5xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={<Users size={20} className="text-blue-600" />}
            iconBg="bg-blue-50"
            label="Total Students"
            value={studentCount ?? 0}
          />
          <StatCard
            icon={<ClipboardCheck size={20} className="text-green-600" />}
            iconBg="bg-green-50"
            label="Attendance Today"
            value={attendanceToday ?? 0}
          />
          <div id="tour-sync-section" className="bg-white border border-divider rounded-xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-amber-50">
                  <BookMarked size={20} className="text-amber-600" />
                </div>
                <span className="text-sm font-medium text-graphite font-secondary">Pending Sync</span>
              </div>
              <p className="text-3xl font-bold text-ink mb-4">{pendingSyncCount}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => triggerSync()}
                disabled={isSyncing || pendingSyncCount === 0 || !navigator.onLine}
                className="flex-1 flex items-center justify-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                Force Sync
              </button>
              <button
                onClick={() => navigate('/sync-hub')}
                className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-3 py-2 text-xs font-semibold transition-colors"
              >
                <QrCode size={14} />
                Share via QR
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ── Assigned Classes ── */}
        <div id="tour-assigned-classes" className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-ink mb-4">Assigned Classes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {assignedClasses.map((cls) => (
              <Link
                key={`${cls.id}-${cls.subjectId}`}
                to={`/teacher/register/${encodeURIComponent(cls.id)}/${cls.subjectId}`}
                className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-ink">{cls.name}</h3>
                    <p className="text-sm font-medium text-action font-secondary">{cls.subject}</p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-indigo-50 group-hover:text-action transition-colors">
                    <ChevronRight size={18} className="text-graphite group-hover:text-action" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-graphite">
                  <Users size={14} />
                  <span className="font-secondary">{cls.students} Students</span>
                </div>
              </Link>
            ))}
          </div>

          {/* ── Class Teacher Section ── */}
          {teacherProfile?.isClassTeacher && teacherProfile?.classTeacherOf && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                <FileText size={20} className="text-indigo-600" />
                Class Teacher Tools ({teacherProfile.classTeacherOf})
              </h2>
              <div id="tour-class-tools" className="bg-white border border-indigo-100 rounded-xl p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-ink">Generate Report Cards</h3>
                    <p className="text-sm text-graphite mt-1 mb-4">
                      Check marks completion status and generate CBSE formatted report cards for your entire class.
                    </p>
                    <div className="flex gap-3 mb-4 sm:mb-0">
                      <div>
                        <label className="block text-xs font-medium text-graphite mb-1">Academic Year</label>
                        <select
                          value={academicYear}
                          onChange={(e) => setAcademicYear(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-32 bg-gray-50"
                        >
                          <option value="2025-26">2025-26</option>
                          <option value="2026-27">2026-27</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-graphite mb-1">Section</label>
                        <select
                          value={section}
                          onChange={(e) => setSection(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-24 bg-gray-50"
                        >
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto sm:mt-8">
                    <ReportCardDownloadButton
                      classId={teacherProfile.classTeacherOf}
                      students={students.filter(s => {
                        const sClass = s.class?.toString() || '';
                        const sSection = s.section || '';
                        let isCorrectClass = false;
                        if (teacherProfile.classTeacherOf === 'Class VI') isCorrectClass = ['6','VI'].includes(sClass.toUpperCase());
                        else if (teacherProfile.classTeacherOf === 'Class VII') isCorrectClass = ['7','VII'].includes(sClass.toUpperCase());
                        else if (teacherProfile.classTeacherOf === 'Class VIII') isCorrectClass = ['8','VIII'].includes(sClass.toUpperCase());
                        else if (teacherProfile.classTeacherOf === 'Class IX') isCorrectClass = ['9','IX'].includes(sClass.toUpperCase());
                        else if (teacherProfile.classTeacherOf === 'Class X') isCorrectClass = ['10','X'].includes(sClass.toUpperCase());
                        else isCorrectClass = true;

                        return isCorrectClass && (!sSection || sSection.toUpperCase() === section.toUpperCase());
                      })}
                      academicYear={academicYear}
                      section={section}
                    />
                    <Link
                      to={`/teacher/coscholastic/${encodeURIComponent(teacherProfile.classTeacherOf)}`}
                      className="mt-4 flex items-center justify-center gap-2 w-full bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors shadow-sm"
                    >
                      <ClipboardCheck size={16} />
                      Enter Co-Scholastic Grades
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Recent Activity ── */}
        <div id="tour-recent-activity">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">Recent Activity</h2>
            <Link to={user?.role === 'admin' ? "/admin/history" : "/teacher/history"} className="text-sm font-medium text-action hover:underline">
              View History & Undo
            </Link>
          </div>
          <div className="bg-white border border-divider rounded-xl p-5">
            <ul className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, i) => (
                  <li key={activity.id || i} className="flex items-start gap-3">
                    <div className="mt-0.5 p-1.5 rounded-full bg-green-50 text-green-600">
                      <RefreshCw size={14} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink">{activity.details}</p>
                      <div className="flex items-center gap-1 text-xs text-graphite mt-1">
                        <Clock size={12} />
                        <span>{formatTimestamp(activity.timestamp)}</span>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-sm text-graphite text-center py-4">
                  No recent activity. Start entering marks or taking attendance.
                </li>
              )}
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
}

function resolveClassCohort(classStr) {
  if (!classStr) return 'Class VI';
  const upper = String(classStr).toUpperCase().trim();
  // We strip any section suffix like '-A', '-B' to match the base class.
  const baseClass = upper.split('-')[0].trim();
  
  if (['6','VI','CLASS VI'].includes(baseClass)) return 'Class VI';
  if (['7','VII','CLASS VII'].includes(baseClass)) return 'Class VII';
  if (['8','VIII','CLASS VIII'].includes(baseClass)) return 'Class VIII';
  if (['9','IX','CLASS IX'].includes(baseClass)) return 'Class IX';
  if (['10','X','CLASS X'].includes(baseClass)) return 'Class X';
  
  // Fallback pattern matching if direct exact match fails
  if (baseClass.includes('VIII') || baseClass.includes('8')) return 'Class VIII';
  if (baseClass.includes('VII') || baseClass.includes('7')) return 'Class VII';
  if (baseClass.includes('VI') || baseClass.includes('6')) return 'Class VI';
  if (baseClass.includes('IX') || baseClass.includes('9')) return 'Class IX';
  if (baseClass.includes('X') || baseClass.includes('10')) return 'Class X';
  
  return 'Class VI'; // default fallback
}

/* ── Helper: Format timestamp for display ── */
function formatTimestamp(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/* ── Helper Components (internal) ────────────────────── */

function StatCard({ icon, iconBg, label, value }) {
  return (
    <div className="bg-white border border-divider rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
        <span className="text-sm font-medium text-graphite font-secondary">{label}</span>
      </div>
      <p className="text-3xl font-bold text-ink">{value}</p>
    </div>
  );
}

